import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

import { denormalisedResponseEntities } from '../../util/data';
import { storableError } from '../../util/errors';
import { fetchCurrentUser, setCurrentUser } from '../../ducks/user.duck';
import {
  STOCK_LOCATIONS_PRIVATE_KEY,
  canAddLocation,
  getStockLocations,
  buildLocationRecord,
  upsertLocation,
  setDefaultLocation,
  archiveLocation,
  restoreLocation,
  deleteLocation,
} from '../../util/custom/stockLocations';

/**
 * CL-STOCK-01 — the seller's address book.
 *
 * Every write is read-modify-write on the WHOLE array: Sharetribe merges
 * privateData at the top level but replaces the value of a key, and there is no
 * append and no version field to lock on (N4, SL11). So each thunk re-reads the
 * current user, applies one pure operation from util/custom/stockLocations, and
 * writes the result back.
 *
 * D3 — this duck must never call sdk.ownListings.*. Editing, archiving or
 * deleting a saved location changes no listing, ever. AC24 pins it.
 */

const writeLocations = async (locations, dispatch, sdk) => {
  const response = await sdk.currentUser.updateProfile(
    { privateData: { [STOCK_LOCATIONS_PRIVATE_KEY]: locations } },
    { expand: true, include: ['profileImage'] }
  );
  const entities = denormalisedResponseEntities(response);
  if (entities.length !== 1) {
    throw new Error('Expected a resource in the sdk.currentUser.updateProfile response');
  }
  dispatch(setCurrentUser(entities[0]));
  return getStockLocations(entities[0]);
};

/**
 * Re-read the seller's own locations straight from the API, so a stale Redux
 * copy can never overwrite a location saved in another tab.
 */
const currentLocations = async sdk => {
  const response = await sdk.currentUser.show();
  const entities = denormalisedResponseEntities(response);
  return getStockLocations(entities[0]);
};

/**
 * Every write is read-modify-write of the WHOLE array, so two of them running at once
 * both read the same "before" and the second one's write erases the first. Archive row A
 * and then act on row B a moment later and row A comes back.
 *
 * Disabling rows in the UI cannot fix this — the wizard's add-location dialog writes
 * through the same thunks and knows nothing about the settings page's rows. So the
 * writes are serialised here, at the one place all of them pass through: each waits for
 * the previous one to settle, then does its own read. The read is inside the queue on
 * purpose; a read taken before waiting would be exactly the stale copy we are avoiding.
 *
 * Module-level state is safe here because these thunks only ever run from a user action
 * in the browser. `loadData` dispatches nothing from this file, so a server render never
 * touches this queue.
 */
let writeQueue = Promise.resolve();
const serialiseWrite = task => {
  // `.then(task, task)` so a rejected predecessor still lets the next write run.
  const result = writeQueue.then(task, task);
  // The queue itself must never hold a rejection, or every later write inherits it.
  writeQueue = result.then(() => null, () => null);
  return result;
};

/** Marks a refusal we made ourselves, so the UI can say why instead of "save failed". */
export const CAP_REACHED = 'stockLocations/capReached';

const mutation = (name, apply, guard = null) =>
  createAsyncThunk(
    `StockLocationsPage/${name}`,
    async (args, { dispatch, extra: sdk, rejectWithValue }) => {
      try {
        return await serialiseWrite(async () => {
          const locations = await currentLocations(sdk);
          // Guards run on the freshly read array, inside the queue — a cap checked
          // against a stale copy is not a cap.
          const refusal = guard ? guard(locations, args) : null;
          if (refusal) {
            const error = new Error(refusal);
            error.refusal = refusal;
            throw error;
          }
          return await writeLocations(apply(locations, args), dispatch, sdk);
        });
      } catch (e) {
        return rejectWithValue(
          e?.refusal ? { type: 'error', refusal: e.refusal } : storableError(e)
        );
      }
    }
  );

/**
 * SL6 / EC13: the cap protects the extended-data size limit, so it has to hold at the
 * write, not only on the button that happens to be in front of the seller. The settings
 * page disables "Add a location" at the cap, but the wizard's add-location dialog and
 * Restore both bypassed that button entirely.
 *
 * Adding is a cap check; editing an existing record is not, because it does not change
 * how many are active.
 */
const capGuard = (locations, args) =>
  !args?.existingId && !canAddLocation(locations) ? CAP_REACHED : null;

export const saveLocationThunk = mutation(
  'saveLocation',
  (locations, { draft, existingId }) => {
    const existing = existingId ? locations.find(l => l.id === existingId) || null : null;
    const record = buildLocationRecord(draft, {
      // crypto.randomUUID() is available in the browser and in Node 22 — no new
      // dependency. It is passed in so the domain module stays pure.
      id: crypto.randomUUID(),
      now: new Date().toISOString(),
      existing,
    });
    return upsertLocation(locations, record);
  },
  capGuard
);

export const chooseDefaultLocationThunk = mutation('chooseDefault', (locations, { id }) =>
  setDefaultLocation(locations, id)
);

export const archiveSavedLocationThunk = mutation('archive', (locations, { id }) =>
  archiveLocation(locations, id, new Date().toISOString())
);

export const restoreSavedLocationThunk = mutation(
  'restore',
  (locations, { id }) => restoreLocation(locations, id, new Date().toISOString()),
  // Restoring turns an archived record back into an active one, so it is subject to the
  // cap exactly like adding is. There is no `existingId` in these args, so capGuard's
  // "editing is exempt" branch does not apply.
  capGuard
);

export const deleteSavedLocationThunk = mutation('delete', (locations, { id }) =>
  // Pass `now` like archive and restore do. A soft-deleted record keeps its place in
  // the array, so `updatedAt` is the only field that can say when it was deleted.
  deleteLocation(locations, id, new Date().toISOString())
);

/**
 * How many of the seller's own listings already carry this location — the number
 * SL8 puts in the edit and delete wording.
 *
 * Read-only, and deliberately client-side: no search schema is registered for
 * publicData.stockLocationId (N3), and none is needed, because ownListings.query
 * returns the seller's own listings with their publicData. A query is not an
 * update, so AC24 still holds — this duck must never call ownListings.update.
 *
 * Counts the first page only. The number is wording, not a guarantee, so an
 * approximate count for a seller with hundreds of listings is acceptable; it must
 * never block or fail a save.
 *
 * Two economies, because a seller opens these dialogs repeatedly:
 *   - sparse fields — only `publicData` is read, where the previous query pulled up to
 *     100 complete listings with their images and availability plans to count one key;
 *   - a cache for the visit, keyed by location id. Counting is not a write, and every
 *     write goes through the thunks above, so `countsByLocationId` is dropped whenever
 *     one of them lands rather than being kept in step with it.
 */
// NOTE the prefix: `StockLocationsRead/`, not `StockLocationsPage/`. The reducer
// below matches every `StockLocationsPage/` action to drive saveInProgress, and a
// read must not make the page look like it is saving.
export const countListingsUsingLocationThunk = createAsyncThunk(
  'StockLocationsRead/countListings',
  async ({ id }, { extra: sdk, getState }) => {
    const cached = getState()?.StockLocationsPage?.countsByLocationId?.[id];
    if (typeof cached === 'number') return cached;
    try {
      const response = await sdk.ownListings.query({
        perPage: 100,
        'fields.ownListing': ['publicData'],
      });
      const listings = denormalisedResponseEntities(response);
      return listings.filter(l => l?.attributes?.publicData?.stockLocationId === id).length;
    } catch (e) {
      // Never let a count failure stop the seller editing a location.
      return 0;
    }
  }
);

// Thin wrappers, matching the template's own duck style.
export const saveLocation = args => dispatch => dispatch(saveLocationThunk(args)).unwrap();
export const chooseDefaultLocation = args => dispatch =>
  dispatch(chooseDefaultLocationThunk(args)).unwrap();
export const archiveSavedLocation = args => dispatch =>
  dispatch(archiveSavedLocationThunk(args)).unwrap();
export const restoreSavedLocation = args => dispatch =>
  dispatch(restoreSavedLocationThunk(args)).unwrap();
export const deleteSavedLocation = args => dispatch =>
  dispatch(deleteSavedLocationThunk(args)).unwrap();
export const countListingsUsingLocation = args => dispatch =>
  dispatch(countListingsUsingLocationThunk(args)).unwrap();

const initialState = { saveInProgress: false, saveError: null, countsByLocationId: {} };

const stockLocationsPageSlice = createSlice({
  name: 'StockLocationsPage',
  initialState,
  reducers: {
    clearSaveError: state => {
      state.saveError = null;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(countListingsUsingLocationThunk.fulfilled, (state, action) => {
        state.countsByLocationId[action.meta.arg.id] = action.payload;
      })
      .addMatcher(
        action => action.type.startsWith('StockLocationsPage/') && action.type.endsWith('/pending'),
        state => {
          state.saveInProgress = true;
          state.saveError = null;
        }
      )
      .addMatcher(
        action =>
          action.type.startsWith('StockLocationsPage/') && action.type.endsWith('/fulfilled'),
        state => {
          state.saveInProgress = false;
          state.saveError = null;
          // Any write can change which listings use which location, so the cached
          // counts are dropped wholesale rather than reasoned about per key.
          state.countsByLocationId = {};
        }
      )
      .addMatcher(
        action =>
          action.type.startsWith('StockLocationsPage/') && action.type.endsWith('/rejected'),
        (state, action) => {
          state.saveInProgress = false;
          state.saveError = action.payload || { type: 'error' };
        }
      );
  },
});

export const { clearSaveError } = stockLocationsPageSlice.actions;

export const loadData = () => dispatch => dispatch(fetchCurrentUser());

export default stockLocationsPageSlice.reducer;
